
import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowUp, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";

interface Message {
  id: string;
  content: string;
  sender: "user" | "assistant";
  isLoading?: boolean;
}

interface SettingsData {
  showTitle: string;
  description: string;
  audienceType: string;
  episodeLength: string;
  customWords: string;
  tonality: string;
  socialHandles: {
    twitter: string;
    instagram: string;
    linkedin: string;
  };
  includeTopics: string;
  excludeTopics: string;
}

const defaultMessages: Message[] = [
  {
    id: "welcome",
    content: "Hi there! I'm Agent V, your podcast assistant. I'll help you set up your podcast profile. What's the name of your podcast?",
    sender: "assistant",
  },
];

const questions = [
  { id: "description", question: "Great! Can you provide a brief description of what your podcast is about?" },
  { id: "audience", question: "Who is your target audience for this podcast?" },
  { id: "episodeLength", question: "What is the typical length of your episodes? (e.g., 30-45 minutes)" },
  { id: "customWords", question: "Are there any specific words, phrases, or jargon that are unique to your podcast or topic?" },
  { id: "tonality", question: "How would you describe the tone of your podcast? (e.g., professional, casual, humorous)" },
  { id: "socialTwitter", question: "What's your Twitter/X handle for the podcast? (Skip by pressing Enter if you don't have one)" },
  { id: "socialInstagram", question: "What's your Instagram handle? (Skip by pressing Enter if you don't have one)" },
  { id: "socialLinkedin", question: "What's your LinkedIn profile or page? (Skip by pressing Enter if you don't have one)" },
  { id: "includeTopics", question: "What topics do you typically want to include in your podcast? (comma separated)" },
  { id: "excludeTopics", question: "Are there any topics you prefer to avoid discussing? (comma separated)" },
  { id: "confirmation", question: "Thanks for all that information! I'll use this to set up your podcast profile. Ready to get started with Agent V?" }
];

const OnboardingChat = () => {
  const [messages, setMessages] = useState<Message[]>(defaultMessages);
  const [input, setInput] = useState("");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [settings, setSettings] = useState<SettingsData>({
    showTitle: "",
    description: "",
    audienceType: "",
    episodeLength: "",
    customWords: "",
    tonality: "",
    socialHandles: {
      twitter: "",
      instagram: "",
      linkedin: "",
    },
    includeTopics: "",
    excludeTopics: "",
  });
  const [isComplete, setIsComplete] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    inputRef.current?.focus();
  }, [messages]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  };

  const addMessage = (content: string, sender: "user" | "assistant", isLoading: boolean = false) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      content,
      sender,
      isLoading,
    };
    setMessages((prev) => [...prev, newMessage]);
    return newMessage.id;
  };

  const updateSettings = (answer: string) => {
    switch (currentQuestionIndex) {
      case -1: // Title question (first question)
        setSettings(prev => ({ ...prev, showTitle: answer }));
        break;
      case 0: // Description
        setSettings(prev => ({ ...prev, description: answer }));
        break;
      case 1: // Audience
        setSettings(prev => ({ ...prev, audienceType: answer }));
        break;
      case 2: // Episode Length
        setSettings(prev => ({ ...prev, episodeLength: answer }));
        break;
      case 3: // Custom Words
        setSettings(prev => ({ ...prev, customWords: answer }));
        break;
      case 4: // Tonality
        setSettings(prev => ({ ...prev, tonality: answer }));
        break;
      case 5: // Twitter
        setSettings(prev => ({ 
          ...prev, 
          socialHandles: { ...prev.socialHandles, twitter: answer } 
        }));
        break;
      case 6: // Instagram
        setSettings(prev => ({ 
          ...prev, 
          socialHandles: { ...prev.socialHandles, instagram: answer } 
        }));
        break;
      case 7: // LinkedIn
        setSettings(prev => ({ 
          ...prev, 
          socialHandles: { ...prev.socialHandles, linkedin: answer } 
        }));
        break;
      case 8: // Include Topics
        setSettings(prev => ({ ...prev, includeTopics: answer }));
        break;
      case 9: // Exclude Topics
        setSettings(prev => ({ ...prev, excludeTopics: answer }));
        break;
    }
  };

  const askNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      // We still have questions to ask
      setTimeout(() => {
        addMessage(questions[currentQuestionIndex].question, "assistant");
        setCurrentQuestionIndex(prevIndex => prevIndex + 1);
      }, 800); // Slight delay for more natural feel
    } else if (currentQuestionIndex === questions.length - 1) {
      // This is the last question (confirmation)
      setIsComplete(true);
      
      // Show a completion message
      setTimeout(() => {
        addMessage("Great! I've saved your settings. You're all set to start using Agent V. You can always modify these settings later in the Settings page.", "assistant");
        
        // Save settings to localStorage for demo
        localStorage.setItem("agentv-settings", JSON.stringify(settings));
        
        // Show toast notification
        toast({
          title: "Setup Complete!",
          description: "Your podcast profile has been saved. Let's explore Agent V!",
        });
        
        // Redirect to dashboard after a delay
        setTimeout(() => {
          navigate("/");
        }, 3000);
      }, 1000);
    }
  };

  const handleSendMessage = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!input.trim() && !isComplete) return;

    // Add user message
    addMessage(input, "user");
    
    // For the first question (podcast name)
    if (currentQuestionIndex === 0 && messages.length === 1) {
      updateSettings(input);
      setInput("");
      askNextQuestion();
      return;
    }
    
    // For all other questions
    updateSettings(input);
    setInput("");
    askNextQuestion();
  };

  const handleSkip = () => {
    navigate("/");
    toast({
      title: "Onboarding Skipped",
      description: "You can always configure your settings later.",
    });
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-b from-background to-muted">
      <div className="container mx-auto px-4 flex flex-col h-full py-8">
        <h1 className="text-3xl font-bold mb-6 text-center">Welcome to Agent V</h1>
        
        <Card className="flex-1 flex flex-col overflow-hidden">
          <CardContent className="flex-1 p-4 overflow-hidden flex flex-col">
            <div className="flex-1 overflow-y-auto pr-2">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`mb-4 flex ${message.sender === "assistant" ? "justify-start" : "justify-end"}`}
                >
                  <div
                    className={`rounded-lg px-4 py-2 max-w-[80%] ${
                      message.sender === "assistant"
                        ? "bg-primary/10 text-foreground"
                        : "bg-primary text-primary-foreground"
                    }`}
                  >
                    {message.isLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    )}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSendMessage} className="mt-4 flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={handleInputChange}
                placeholder={isComplete ? "Setup complete..." : "Type your answer here..."}
                className="flex-1"
                disabled={isComplete}
              />
              <Button type="submit" size="icon" disabled={!input.trim() || isComplete}>
                <ArrowUp className="h-4 w-4" />
              </Button>
            </form>
          </CardContent>
        </Card>
        
        <div className="flex justify-center mt-4">
          <Button variant="outline" onClick={handleSkip}>
            Skip Setup
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingChat;
